import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Banknote, CreditCard, History, Search, Wallet, X,
} from 'lucide-react';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { formatCurrency } from '../../data/mockData';
import {
  getPosSale, listPosSales, voidPosSale, mapPosChargeError, mapPosVoidError,
  normalizePosSalePayload, posSalesDateIst, POS_CHARGE_ERRORS,
} from '../../api/pos';
import { isPosReceiptRevised, isPosReceiptVoided, posReceiptRevisionLabel } from '../../utils/escpos';
import styles from './PosHistoryModal.module.css';

const EMPTY_SUMMARY = {
  bill_count: 0,
  gross_total: 0,
  by_payment_method: {},
  truncated: false,
};

const PAYMENT_METHODS = {
  CASH: { label: 'Cash', icon: Banknote },
  UPI: { label: 'UPI', icon: Wallet },
  CARD: { label: 'Card', icon: CreditCard },
};

function saleTimeLabel(sale) {
  const raw = sale.created_at || sale.createdAt || sale.charged_at;
  if (!raw) return '—';
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  });
}

function saleOrderId(sale) {
  return sale.order_id || sale.orderId || sale.id;
}

function saleRecognizedDate(sale) {
  if (sale.recognized_date) return sale.recognized_date;
  const raw = sale.created_at || sale.createdAt || sale.charged_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : posSalesDateIst(date);
}

function saleIsVoided(sale) {
  return isPosReceiptVoided(sale);
}

function saleIsSameDay(sale) {
  return saleRecognizedDate(sale) === posSalesDateIst();
}

/** `HH:MM` on the history day. Empty is unset. Overnight windows are not supported. */
function normalizeHistoryTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${match[1]}:${match[2]}`;
}

function salePhone(sale) {
  return String(sale.customer_phone || sale.phone || '').trim();
}

function summaryMethods(summary) {
  const methods = summary?.by_payment_method || {};
  const keys = [
    ...Object.keys(PAYMENT_METHODS),
    ...Object.keys(methods).filter((key) => !PAYMENT_METHODS[key]),
  ];
  return keys.map((key) => ({
    key,
    ...PAYMENT_METHODS[key],
    label: PAYMENT_METHODS[key]?.label || key,
    count: Number(methods[key]?.count) || 0,
    amount: Number(methods[key]?.amount) || 0,
  }));
}

export default function PosHistoryModal({
  token,
  onClose,
  onOpenSale,
  onEditSale,
  onVoided,
  canEdit = false,
  visible = true,
}) {
  const [date, setDate] = useState(() => posSalesDateIst());
  const [scope, setScope] = useState('mine');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [confirmVoidId, setConfirmVoidId] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPaymentMethod, setVoidPaymentMethod] = useState('CASH');
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const loadSales = useCallback(async ({ cursor, append } = {}) => {
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setActionMessage('');
    }
    setError('');
    const from = normalizeHistoryTime(fromTime);
    const to = normalizeHistoryTime(toTime);
    if (from == null || to == null || (from && to && from >= to)) {
      if (!append) {
        setSales([]);
        setSummary(EMPTY_SUMMARY);
      }
      setNextCursor(null);
      setError(POS_CHARGE_ERRORS.invalid_time_window);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    try {
      const data = await listPosSales({
        date,
        scope,
        from_time: from || undefined,
        to_time: to || undefined,
        q: q || undefined,
        limit: 30,
        cursor,
      }, token);
      const page = Array.isArray(data?.sales) ? data.sales : [];
      setSales((prev) => (append ? [...prev, ...page] : page));
      setSummary(data?.summary && typeof data.summary === 'object'
        ? { ...EMPTY_SUMMARY, ...data.summary }
        : EMPTY_SUMMARY);
      setNextCursor(data?.next_cursor || null);
    } catch (err) {
      if (!append) {
        setSales([]);
        setSummary(EMPTY_SUMMARY);
      }
      setNextCursor(null);
      setError(mapPosChargeError(err));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [date, scope, q, fromTime, toTime, token]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const applySearch = () => {
    setQ(qInput.trim());
  };

  const clearSearch = () => {
    setQInput('');
    setQ('');
  };

  const resetVoidConfirm = () => {
    setConfirmVoidId(null);
    setVoidReason('');
    setVoidPaymentMethod('CASH');
  };

  const openVoidConfirm = (sale) => {
    const orderId = saleOrderId(sale);
    if (!orderId) return;
    setActionMessage('');
    setError('');
    setConfirmVoidId(orderId);
    setVoidReason('');
    const tender = String(sale.payment_method || 'CASH').toUpperCase();
    setVoidPaymentMethod(PAYMENT_METHODS[tender] ? tender : 'CASH');
  };

  const fetchSalePayload = async (sale) => {
    const orderId = saleOrderId(sale);
    if (!orderId) throw new Error('not_found');
    const data = await getPosSale(orderId, token);
    return normalizePosSalePayload(data, sale);
  };

  const handleAction = async (sale, action) => {
    const orderId = saleOrderId(sale);
    if (!orderId || busyKey) return;
    if (action === 'edit' && !canEdit) return;
    if (action === 'edit' && saleIsVoided(sale)) {
      setActionMessage('This bill is already voided — it cannot be edited.');
      return;
    }
    if (action === 'edit' && !saleIsSameDay(sale)) {
      setActionMessage('This bill can only be edited on the same day it was charged.');
      return;
    }
    const key = `${action}:${orderId}`;
    setBusyKey(key);
    setError('');
    setActionMessage('');
    resetVoidConfirm();
    try {
      const payload = await fetchSalePayload(sale);
      if (action === 'edit') onEditSale?.(payload);
      else onOpenSale?.(payload);
      // Keep history mounted so Back from edit/reprint returns here with filters intact.
    } catch (err) {
      setError(mapPosChargeError(err));
    } finally {
      setBusyKey('');
    }
  };

  const handleVoid = async (sale) => {
    const orderId = saleOrderId(sale);
    if (!orderId || busyKey || !canEdit) return;
    if (saleIsVoided(sale)) {
      setActionMessage('Already deleted — refresh the list.');
      resetVoidConfirm();
      return;
    }
    if (!saleIsSameDay(sale)) {
      setActionMessage('Only same IST calendar day as the original bill.');
      resetVoidConfirm();
      return;
    }
    const reason = voidReason.trim();
    if (reason.length > 200) {
      setError(mapPosVoidError({ code: 'invalid_void_reason' }));
      return;
    }
    const key = `void:${orderId}`;
    setBusyKey(key);
    setError('');
    setActionMessage('');
    try {
      const data = await voidPosSale(orderId, {
        reason: reason || undefined,
        payment_method: voidPaymentMethod || undefined,
      }, token);
      const payload = normalizePosSalePayload(data, sale);
      resetVoidConfirm();
      onVoided?.(payload, data);
      await loadSales();
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'already_voided' || code === 'void_conflict' || code === 'insufficient_stock') {
        setError(mapPosVoidError(err));
        await loadSales();
        return;
      }
      setError(mapPosVoidError(err));
    } finally {
      setBusyKey('');
    }
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="pos-history-title">
      <div className={styles.modal}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <h2 id="pos-history-title" className={styles.title}>POS history</h2>
        <p className={styles.subtitle}>
          {canEdit
            ? 'Any IST date for reprint. Edit and Delete (void) only work on the same IST day as the original bill.'
            : 'Any IST date — open a bill to reprint.'}
        </p>

        <div className={styles.filters}>
          <label className={`${styles.field} ${styles.dateField}`}>
            <span className={styles.fieldLabel}>Date (IST)</span>
            <input
              type="date"
              className={styles.input}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className={`${styles.field} ${styles.scopeField}`}>
            <span className={styles.fieldLabel}>Scope</span>
            <select
              className={styles.select}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <option value="mine">My sales</option>
              <option value="store">Whole store</option>
            </select>
          </label>
          <label className={`${styles.field} ${styles.timeField}`}>
            <span className={styles.fieldLabel}>From (IST)</span>
            <input
              type="time"
              className={styles.input}
              value={fromTime}
              step={60}
              onChange={(e) => setFromTime(e.target.value)}
            />
          </label>
          <label className={`${styles.field} ${styles.timeField}`}>
            <span className={styles.fieldLabel}>To (IST)</span>
            <input
              type="time"
              className={styles.input}
              value={toTime}
              step={60}
              onChange={(e) => setToTime(e.target.value)}
            />
          </label>
          {(fromTime || toTime) && (
            <button
              type="button"
              className={styles.clearTime}
              onClick={() => { setFromTime(''); setToTime(''); }}
            >
              Clear time
            </button>
          )}
          <label className={`${styles.field} ${styles.searchField}`}>
            <span className={styles.fieldLabel}>Bill / phone</span>
            <div className={styles.searchRow}>
              <input
                className={styles.input}
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
                placeholder="Bill, order id, or phone number"
              />
              <Button type="button" variant="outline" size="sm" icon={Search} onClick={applySearch}>
                Search
              </Button>
              {(qInput || q) && (
                <button type="button" className={styles.clearSearch} onClick={clearSearch} aria-label="Clear search">
                  <X size={15} />
                </button>
              )}
            </div>
          </label>
        </div>

        {actionMessage && (
          <p className={styles.actionMessage} role="alert">
            <AlertTriangle size={14} />
            {actionMessage}
          </p>
        )}

        {!loading && !error && (
          <>
            <section className={styles.summary} aria-label="Day summary">
              <div className={styles.summaryMain}>
                <span className={styles.summaryLabel}>
                  {scope === 'mine' ? 'My day' : 'Store day'}
                </span>
                <strong>{summary.bill_count} bill{summary.bill_count === 1 ? '' : 's'}</strong>
                <span>{formatCurrency(summary.gross_total)}</span>
              </div>
              <div className={styles.tenders}>
                {summaryMethods(summary).map(({ key, label, icon: Icon, count, amount }) => (
                  <div key={key} className={styles.tender}>
                    <span className={styles.tenderLabel}>
                      {Icon && <Icon size={13} />} {label}
                    </span>
                    <strong>{formatCurrency(amount)}</strong>
                    <span>{count} bill{count === 1 ? '' : 's'}</span>
                  </div>
                ))}
              </div>
            </section>
            {(fromTime || toTime) && (
              <p className={styles.summaryNote}>Totals are for the full day.</p>
            )}
            {q && !(fromTime || toTime) && (
              <p className={styles.summaryNote}>
                Totals exclude voided bills; search only filters the list below.
              </p>
            )}
            {summary.truncated && (
              <p className={styles.warning} role="status">
                <AlertTriangle size={14} />
                This busy day exceeded the summary limit. Totals may be incomplete.
              </p>
            )}
          </>
        )}

        <div className={styles.list}>
          {loading && <p className={styles.status}>Loading sales…</p>}
          {!loading && !error && sales.length === 0 && (
            <EmptyState
              icon={History}
              title="No POS sales"
              description={
                fromTime || toTime
                  ? 'No bills in this time window.'
                  : q
                    ? 'No bills match this search.'
                    : 'No bills for this date and scope.'
              }
            />
          )}
          {!loading && error && (
            <div className={styles.status}>
              <p className={styles.statusError}>{error}</p>
              <Button variant="outline" size="sm" onClick={() => loadSales()}>Retry</Button>
            </div>
          )}
          {!loading && !error && sales.map((sale) => {
            const orderId = saleOrderId(sale);
            const voided = saleIsVoided(sale);
            const revised = !voided && isPosReceiptRevised(sale);
            const revisionLabel = posReceiptRevisionLabel(sale);
            const sameDay = saleIsSameDay(sale);
            const canMutate = canEdit && sameDay && !voided;
            const refundAmount = Number(sale.total) || 0;
            const reprintBusy = busyKey === `reprint:${orderId}`;
            const editBusy = busyKey === `edit:${orderId}`;
            const voidBusy = busyKey === `void:${orderId}`;
            const confirmingVoid = confirmVoidId === orderId;
            return (
              <div key={orderId || sale.display_id} className={styles.rowCard}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTop}>
                    <span className={styles.billId}>{sale.display_id || orderId || '—'}</span>
                    {voided && (
                      <span className={`${styles.badge} ${styles.voidedBadge}`} title="Voided bill">
                        Voided
                      </span>
                    )}
                    {revised && (
                      <span className={styles.badge} title={revisionLabel ? `Revision ${revisionLabel}` : 'Revised bill'}>
                        Revised{revisionLabel ? ` · r${revisionLabel}` : ''}
                      </span>
                    )}
                  </span>
                  <span className={styles.rowMeta}>
                    {saleTimeLabel(sale)} IST
                    {' · '}
                    {sale.payment_method || 'Tender unavailable'}
                    {' · '}
                    {Number(sale.line_count) || 0} line{Number(sale.line_count) === 1 ? '' : 's'}
                    {salePhone(sale) ? ` · ${salePhone(sale)}` : ''}
                  </span>
                </div>
                <div className={styles.rowRight}>
                  <span className={styles.rowTotal}>{formatCurrency(refundAmount)}</span>
                  <div className={styles.rowActions}>
                    {confirmingVoid ? (
                      <div className={styles.voidConfirm}>
                        <p className={styles.voidConfirmText}>
                          Refund {formatCurrency(refundAmount)} and restore stock?
                        </p>
                        <label className={styles.voidField}>
                          <span>Reason (optional)</span>
                          <input
                            type="text"
                            maxLength={200}
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            placeholder="e.g. wrong cart"
                            disabled={voidBusy}
                          />
                        </label>
                        <label className={styles.voidField}>
                          <span>Refund via</span>
                          <select
                            value={voidPaymentMethod}
                            onChange={(e) => setVoidPaymentMethod(e.target.value)}
                            disabled={voidBusy}
                          >
                            <option value="CASH">Cash</option>
                            <option value="UPI">UPI</option>
                            <option value="CARD">Card</option>
                          </select>
                        </label>
                        <div className={styles.voidConfirmActions}>
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            disabled={Boolean(busyKey)}
                            onClick={() => handleVoid(sale)}
                          >
                            {voidBusy ? 'Voiding…' : 'Confirm delete'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={voidBusy}
                            onClick={resetVoidConfirm}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={Boolean(busyKey)}
                          onClick={() => handleAction(sale, 'reprint')}
                        >
                          {reprintBusy ? 'Opening…' : 'Reprint'}
                        </Button>
                        {canMutate && (
                          <Button
                            type="button"
                            size="sm"
                            disabled={Boolean(busyKey)}
                            onClick={() => handleAction(sale, 'edit')}
                          >
                            {editBusy ? 'Loading…' : 'Edit'}
                          </Button>
                        )}
                        {canMutate && (
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            disabled={Boolean(busyKey)}
                            onClick={() => openVoidConfirm(sale)}
                          >
                            Delete
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {nextCursor && !loading && !error && (
          <div className={styles.footer}>
            <Button
              variant="outline"
              size="sm"
              disabled={loadingMore}
              onClick={() => loadSales({ cursor: nextCursor, append: true })}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
